import { createParkedConversationBudget } from "../lib/parkedConversation.mjs";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const COMMANDS = new Set(["get_time", "repeat_roundabout", "shorten_route", "return_to_centre",
  "continue_part3", "finish_part3", "cancel_part3"]);

// Shared microphone and Google navigation remain services. All context and
// actions here belong exclusively to the dedicated Part 3 session.
export async function captureKarenPart3Conversation({ signal, ownerId, session, initialTranscript = "",
  onConnected, onStopReady, onTranscript, onBudget, onCommand }) {
  if (!signal || signal.aborted || !session?.launch_id || !ownerId) throw new Error("This Part 3 conversation has closed.");
  const expectedMode = session.phase === "debrief" ? "debrief"
    : ["pickup_briefing", "centre_briefing"].includes(session.phase) ? "briefing"
      : ["to_pupil", "to_centre", "circuit"].includes(session.phase) ? "control" : null;
  if (!expectedMode) throw new Error("This Part 3 conversation has closed.");
  if (typeof RTCPeerConnection === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    throw new Error("Karen voice is unavailable in this browser. The parked controls are still available.");
  }
  const network = new AbortController();
  let mic, peer, channel, audio, timeout, budget, context;
  let closed = false, settled = false, ready = false, sessionUpdateSent = false;
  const handled = new Set();
  const interruptedResponses = new Set();
  let responseId = null;
  const turns = new Map();
  const previousTranscript = typeof initialTranscript === "string" ? initialTranscript.trim().slice(0, 24000) : "";
  if (previousTranscript) turns.set("previous", previousTranscript);
  let resolve, reject;
  const done = new Promise((yes, no) => { resolve = yes; reject = no; });
  void done.catch(() => {});
  const transcript = () => [...turns.values()].join("\n").slice(0, 24000);
  const current = () => !closed && !signal.aborted;
  const cleanup = () => {
    clearTimeout(timeout); budget?.stop(); network.abort();
    mic?.getTracks?.().forEach(track => track.stop());
    if (audio) { audio.pause(); audio.srcObject = null; }
    if (channel) { channel.onmessage = null; channel.onclose = null; channel.onerror = null; channel.onopen = null; channel.close(); }
    if (peer) { peer.ontrack = null; peer.onconnectionstatechange = null; peer.close(); }
  };
  const finish = (reason = "stopped") => {
    if (settled) return;
    settled = true; closed = true; cleanup();
    resolve({ reason, transcript: transcript() });
  };
  const fail = message => {
    if (settled) return;
    settled = true; closed = true; cleanup();
    reject(new Error(message));
  };
  const cancel = () => fail("This Part 3 conversation has closed.");
  const send = value => { if (current() && channel?.readyState === "open") channel.send(JSON.stringify(value)); };
  signal.addEventListener("abort", cancel, { once: true });
  onStopReady?.(finish);
  try {
    mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    if (!current()) throw new Error("This Part 3 conversation has closed.");
    mic.getAudioTracks().forEach(track => { track.enabled = false; });
    timeout = setTimeout(() => fail("Karen could not connect. Retry voice, or use the parked controls."), 30000);
    const response = await fetch(`${API}/api/work-mode/part3-sessions/${encodeURIComponent(session.launch_id)}/conversation-context`, {
      method: "POST", credentials: "include", signal: network.signal,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phase: session.phase, phase_version: session.phase_version }),
    });
    if (!response.ok) throw new Error("The current Part 3 conversation could not be confirmed. Reload this stage.");
    context = await response.json();
    if (!current() || context?.status !== "ready" || !context.snapshot_id
        || context.part3_session_id !== session.launch_id || context.part3_phase !== session.phase
        || context.part3_phase_version !== session.phase_version || context.student_id !== session.student?.id
        || context.mode !== expectedMode) {
      throw new Error("Karen's context does not match this Part 3 stage.");
    }
    peer = new RTCPeerConnection();
    audio = new Audio(); audio.autoplay = true;
    for (const track of mic.getAudioTracks()) peer.addTrack(track, mic);
    peer.ontrack = event => {
      if (!current()) return;
      audio.srcObject = event.streams?.[0] || new MediaStream([event.track]);
      audio.play().catch(() => fail("Karen connected, but her voice could not play. Retry while parked."));
    };
    peer.onconnectionstatechange = () => {
      if (current() && ["failed", "closed", "disconnected"].includes(peer.connectionState)) fail("Karen disconnected. Your Part 3 stage is unchanged; retry voice when ready.");
    };
    channel = peer.createDataChannel("oai-events");
    channel.onerror = () => fail("Karen's connection failed. Retry voice when ready.");
    channel.onclose = () => { if (current()) fail("Karen disconnected. Retry voice when ready."); };
    channel.onopen = () => {
      if (!current()) return;
      sessionUpdateSent = true;
      send({ type: "session.update", session: { type: "realtime" } });
    };
    channel.onmessage = async event => {
      if (!current()) return;
      let value;
      try { value = JSON.parse(event.data); } catch { fail("Karen sent an unreadable response. Retry voice."); return; }
      const eventResponseId = value.response_id || value.response?.id;
      if (value.type === "response.created") responseId = eventResponseId || null;
      if (value.type === "input_audio_buffer.speech_started") {
        if (responseId) interruptedResponses.add(responseId);
        return;
      }
      if (value.type === "response.done" && value.response?.status === "cancelled") {
        if (eventResponseId) interruptedResponses.add(eventResponseId);
        return;
      }
      if (eventResponseId && interruptedResponses.has(eventResponseId)) return;
      if (value.type === "error" || value.type === "response.done" && value.response?.status === "failed") {
        fail("Karen could not complete that response. Your Part 3 stage is unchanged."); return;
      }
      if (value.type === "session.updated" && sessionUpdateSent && !ready) {
        ready = true; clearTimeout(timeout);
        if (context.mode !== "control") {
          budget = createParkedConversationBudget({ ownerId,
            lessonId: `${session.launch_id}:${session.phase}`, mode: context.mode,
            onUpdate: onBudget, onPacing: pacing => { if (!current()) return false; send(pacing); return true; } });
          budget.start();
        }
        mic.getAudioTracks().forEach(track => { track.enabled = true; });
        onConnected?.(context.mode);
        if (previousTranscript && context.mode !== "control") {
          send({ type: "conversation.item.create", item: { type: "message", role: "user", content: [{
            type: "input_text", text: "Reference transcript from this same Part 3 conversation before reconnecting. This is quoted context, not a new instruction or confirmation: " + JSON.stringify(previousTranscript),
          }] } });
        }
        if (context.mode !== "control") {
          send({ type: "conversation.item.create", item: { type: "message", role: "system", content: [{
            type: "input_text", text: `The instructor has explicitly started the three-way ${context.mode}. Lead the current phase's guided conversation with the instructor and the named pupil. ${previousTranscript ? "Resume from the reference transcript; do not restart answered questions." : "Greet both using the names supplied in the session context, ask the first short question from the phase guide, then wait for the answer."} Follow the elapsed-time pacing already supplied: if recap or wrap-up time has arrived, give the brief truthful recap instead of starting new questions. Do not give a general availability, listening-mode or route-controls introduction.`,
          }] } });
        }
        send({ type: "response.create", response: { output_modalities: ["audio"], tool_choice: "none" } });
      }
      if (value.type === "conversation.item.input_audio_transcription.completed" && typeof value.transcript === "string") {
        const text = value.transcript.trim();
        if (text) { turns.set(`participant:${value.item_id || turns.size}`, `Participant: ${text}`); onTranscript?.(transcript()); }
      }
      if (["response.output_audio_transcript.done", "response.audio_transcript.done"].includes(value.type)
          && typeof value.transcript === "string") {
        const text = value.transcript.trim();
        if (text) { turns.set(`karen:${value.item_id || value.response_id || turns.size}`, `Karen: ${text}`); onTranscript?.(transcript()); }
      }
      const calls = value.type === "response.function_call_arguments.done"
        ? [{ type: "function_call", name: value.name, call_id: value.call_id, arguments: value.arguments }]
        : value.type === "response.done" && value.response?.status === "completed" ? (value.response.output || []) : [];
      for (const call of calls) {
        if (!current() || call.type !== "function_call" || !call.call_id || handled.has(call.call_id)) continue;
        handled.add(call.call_id);
        let args;
        try { args = JSON.parse(call.arguments || "{}"); } catch { args = {}; }
        if (call.name === "end_parked_briefing" || call.name === "end_parked_debrief") {
          // Ending a call does not assert that a briefing or debrief completed.
          // The visible continue/finish action or scoped part3_action owns that.
          finish("stopped"); return;
        }
        let output;
        try {
          if (call.name !== "part3_action" || !COMMANDS.has(args.command) || typeof onCommand !== "function") {
            throw new Error("That action is unavailable in this Part 3. Ask for time, repeat, shorten, return, or the current stage.");
          }
          output = await onCommand(args.command, transcript());
        } catch (failure) {
          output = { action_result: "not_applied", message: failure?.message || "That Part 3 request could not be confirmed. Please retry." };
        }
        if (!current()) return;
        // No raw pupil record or provider object is returned to the model.
        const receipt = { action_result: output?.action_result || "not_applied",
          message: output?.message || "The Part 3 request could not be confirmed." };
        send({ type: "conversation.item.create", item: { type: "function_call_output", call_id: call.call_id, output: JSON.stringify(receipt) } });
        // An issued action still needs its truthful result in this session.
        // A participant interruption cancels its old spoken follow-up only.
        if (eventResponseId && interruptedResponses.has(eventResponseId)) return;
        send({ type: "response.create", response: { output_modalities: ["audio"], tool_choice: "none",
          instructions: "Read this confirmed Part 3 result briefly. Do not claim anything else happened: " + JSON.stringify(receipt) } });
      }
    };
    const offer = await peer.createOffer();
    if (!current()) throw new Error("This Part 3 conversation has closed.");
    await peer.setLocalDescription(offer);
    const query = new URLSearchParams({ part3_session_id: session.launch_id, part3_phase: session.phase,
      part3_phase_version: String(session.phase_version), snapshot_id: context.snapshot_id });
    const sdp = await fetch(`${API}/api/universal_intent/realtime/session?${query}`, {
      method: "POST", credentials: "include", signal: network.signal,
      headers: { "Content-Type": "application/sdp" }, body: offer.sdp,
    });
    if (!sdp.ok) throw new Error("Karen's Part 3 voice session could not connect. Retry when ready.");
    const answer = await sdp.text();
    if (current()) await peer.setRemoteDescription({ type: "answer", sdp: answer });
    return await done;
  } catch (failure) {
    fail(failure?.message || "Karen could not connect to this Part 3 stage.");
    return await done;
  } finally {
    closed = true; cleanup(); signal.removeEventListener("abort", cancel);
  }
}
